import type { SellerCollectsConfig } from '@b-stock/fe-core/bstock-next/queries'
import { StoreFrontOptionsSellerCollectsDtoShippingArrangedByEnum as ShippingArrangedBy } from '@b-stock/listing-api-client'

import type { SaleKind } from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider/BidConfirmationModal/sellerCollectsBidCopy'
import type { FeeModel } from '@/buyer-portal/helpers/sellerCollectsFeeModel'
import { resolveShippingArrangedBy } from '@/buyer-portal/helpers/sellerCollectsShippingMode'

import { paysViaSellerRep } from './acceptedPaymentMethods'

/**
 * One source of copy for "what happens if you win", shared by the listing-page
 * banner (FP-3148), the bid confirmation modal (FP-3150) and the first-win
 * modal on the order page (FP-3159). They describe the same sequence at three
 * moments, so they must not drift.
 *
 * What is shared is which steps appear and in what order — not how each one is
 * laid out. The banner and the first-win modal give every step a title over its
 * body; the bid confirmation modal lists bodies alone, because it is a summary
 * inside a modal that already has its own heading. That difference is what
 * each design asks for, not an oversight.
 *
 * The wording is not shared either: each surface reads its titles and bodies
 * from its own namespace — `Listing.sellerCollects.banner.steps.titles.<key>`
 * here, `OrderDetailsLayout.sellerCollectsFirstWin.steps.<key>.title` on the
 * order page — because the banner speaks before the win and the modal after
 * it. They diverge on the fee step: LDP-2 has the banner say "B-Stock invoices
 * its fee", while the first-win modal's own design says "Pay your B-Stock
 * fee", which is the step the buyer can act on by then. Everything else is
 * word-for-word identical, so changing one of those and not the other is a
 * drift nothing will catch. Check both before editing either.
 */
const ns = 'Listing.sellerCollects.banner.steps'

const SHIPPING_STEP_KEYS: Record<string, string> = {
  [ShippingArrangedBy.Buyer]: 'shipping.buyer',
  [ShippingArrangedBy.SellerDelivers]: 'shipping.sellerDelivers',
  [ShippingArrangedBy.BookedThroughBstock]: 'shipping.bookedThroughBstock',
}

/**
 * The keys only `mergeSellerPaymentSteps` can produce.
 *
 * Exported so a surface can recognise the merged step without matching on the
 * shape of the key: `payTheSeller` also starts with "pay" but is the unmerged
 * step, and must not pick up the merged step's extra copy.
 */
const MERGED_SELLER_PAYMENT_STEP_KEYS = [
  'payInvoiced',
  'payWireInEmail',
] as const

/** Whether this step is the merged "pay the seller" step. */
export const isMergedSellerPaymentStep = (key: string): boolean =>
  (MERGED_SELLER_PAYMENT_STEP_KEYS as readonly string[]).includes(key)

/**
 * Whether this step is the one telling the buyer to pay the seller, in any of
 * its forms.
 *
 * Wider than {@link isMergedSellerPaymentStep}: the rep-settled step is not a
 * merge of two others, but it is still where ORD-2 puts "questions go to".
 */
export const isSellerPaymentStep = (key: string): boolean =>
  isMergedSellerPaymentStep(key) || key === 'payViaRep'

type WinStepOptions = {
  /**
   * Collapses "the seller invoices you" and "you pay the seller" into one
   * step. The first-win modal's design shows a single "Pay {seller}" step;
   * the pre-bid banner keeps them apart, which product confirmed on FP-3148.
   */
  mergeSellerPaymentSteps?: boolean
  /**
   * Appends the step describing what B-Stock is and is not told. Only the
   * post-win surface shows it: before the gavel there is nothing to track.
   */
  withTracking?: boolean
  /**
   * From the storefront. Only an explicit `false` is a refusal, matching
   * `refusesDisputes` — an unconfigured storefront disputes normally.
   */
  disputesFacilitated?: boolean
  /**
   * The order's own shipment type, which outranks the storefront's policy.
   *
   * Only the post-win surface passes it: the listing banner and the bid
   * confirmation modal speak before any order exists, so the storefront's
   * arrangement is genuinely all there is to describe there.
   */
  shipmentType?: string | null
  /**
   * Adds the proof-of-payment upload step after paying the seller. Callers
   * pass `useAsksForProofOfPayment`, so the step shows only where the buyer
   * can reach the upload.
   */
  asksForProofOfPayment?: boolean
}

/**
 * Whether the storefront makes contact itself once payment clears.
 *
 * A storefront that invoices the buyer and publishes a portal has a channel to
 * the buyer already and uses it; one that does neither leaves the buyer to go
 * and book a slot. The banner, the first-win modal and the pay card's hand-off
 * sentence all turn on this, so they ask here rather than each restating it.
 */
export const resolveSellerReachesOut = (
  config: SellerCollectsConfig | undefined
): boolean =>
  config?.sellerInvoicesBuyer === true &&
  typeof config.paymentInstructionsUrl === 'string' &&
  config.paymentInstructionsUrl.trim() !== ''

/**
 * Whether the seller coordinates pickup itself once payment clears.
 *
 * The storefront's own arrangement, never the order's: a seller who books
 * freight through B-Stock promised nobody a call about a pickup, and an order
 * the buyer happened to collect does not turn that into a promise.
 */
export const resolveSellerCoordinatesPickup = (
  config: SellerCollectsConfig | undefined
): boolean =>
  resolveSellerReachesOut(config) &&
  // Through the shared resolver, with no shipment type: the storefront's own
  // arrangement, read the way every other surface reads it, so a mode this
  // ever has to normalise cannot be normalised here and not there.
  resolveShippingArrangedBy(config) === ShippingArrangedBy.Buyer

/**
 * The step keys for this storefront, generated from its configuration rather
 * than hardcoded per seller.
 *
 * Keys are returned unqualified so each surface can render them from its own
 * namespace: the listing page speaks before the win, the order page after it.
 * The branching lives here once, whichever tense is applied to it.
 *
 * A storefront that does not invoice the buyer points at the B-Stock win email
 * instead; a fee-free one drops the fee step; an unresolvable fee model gets a
 * step that commits to nothing.
 */
export const getSellerCollectsWinStepKeys = (
  config: SellerCollectsConfig | undefined,
  feeModel: FeeModel,
  {
    withTracking = false,
    disputesFacilitated,
    mergeSellerPaymentSteps = false,
    shipmentType,
    asksForProofOfPayment = false,
  }: WinStepOptions = {}
): string[] => {
  const mode = resolveShippingArrangedBy(config, shipmentType)
  // B-Stock books the freight itself here, so the carrier reports movement
  // back and this page really does stay current. An order the buyer collected
  // is not tracked whatever the storefront usually does.
  const carrierTracked = mode === ShippingArrangedBy.BookedThroughBstock
  const disputes =
    disputesFacilitated === false ? 'disputesRefused' : 'disputes'
  const tracking = carrierTracked ? 'carrierTracked' : 'untracked'

  // Merged, the step has to carry both halves, so it gets its own key rather
  // than reusing either of the two it replaces.
  //
  // A storefront that sends no invoice merges on every surface, asked for or
  // not: there is no invoice to wait for, so "they invoice you" and "you pay
  // them" are one act. Its design shows three banner steps where an invoicing
  // seller shows four.
  // A storefront settled through a rep merges too, and says so: there is no
  // invoice and no channel the buyer drives, so the design gives these sellers
  // one step naming the representative.
  const sellerSendsNoInvoice = config?.sellerInvoicesBuyer === false
  const viaRep = paysViaSellerRep(config?.acceptedPaymentMethods)
  const sellerPayment = viaRep
    ? ['payViaRep']
    : mergeSellerPaymentSteps || sellerSendsNoInvoice
      ? [
          sellerSendsNoInvoice
            ? MERGED_SELLER_PAYMENT_STEP_KEYS[1]
            : MERGED_SELLER_PAYMENT_STEP_KEYS[0],
        ]
      : ['sellerInvoices', 'payTheSeller']

  return [
    ...sellerPayment,
    ...(asksForProofOfPayment ? ['proofOfPayment'] : []),
    ...(feeModel === 'feeBearing' ? ['bstockFee'] : []),
    ...(feeModel === 'unknownFee' ? ['bstockFeeUnknown'] : []),
    // A storefront that both invoices the buyer and publishes a portal makes
    // contact itself once payment clears, so the buyer is told to expect that
    // rather than to go and book a slot. Same signal the pay card uses for
    // its own hand-off sentence.
    resolveSellerCoordinatesPickup(config)
      ? 'shipping.sellerCoordinates'
      : ((mode && SHIPPING_STEP_KEYS[mode]) ?? 'shipping.unconfigured'),
    ...(withTracking ? [`tracking.${tracking}.${disputes}`] : []),
  ]
}

/**
 * The three steps that name the auction outcome. Buy Now shows the same
 * sequence, but there is no win to be after, so those get their own wording.
 */
const PURCHASE_VARIANT_STEP_KEYS = new Set([
  'payInvoiced',
  'payViaRep',
  'shipping.bookedThroughBstock',
  'payInvoicedNoMethods',
  'payWireInEmail',
  'shipping.sellerCoordinates',
  'shipping.unconfigured',
])

/** Exported for the test that proves every variant exists in the locale. */
export const purchaseVariantStepKeys = (): string[] => [
  ...PURCHASE_VARIANT_STEP_KEYS,
]

/**
 * Which surface is reading these steps.
 *
 * The banner and the bid/Buy Now modals share this namespace, and a step the
 * modals word differently needs to say so — they are not two tenses of one
 * sentence, which is what `SaleKind` covers.
 */
export type StepSurface = 'banner' | 'modal'

/**
 * Steps the confirmation modals word differently from the banner.
 *
 * The modals quote the carrier B-Stock picked a few lines above these steps,
 * so the banner's "{seller} is the carrier" would contradict the name beside
 * it. The banner has no quote to contradict.
 */
const MODAL_VARIANT_STEP_KEYS = new Set(['shipping.bookedThroughBstock'])

/** Exported for the test that proves every variant exists in the locale. */
export const modalVariantStepKeys = (): string[] => [...MODAL_VARIANT_STEP_KEYS]

/** One step as a message id in the tense of this sale. */
export const sellerCollectsWinStepId = (
  key: string,
  saleKind: SaleKind = 'bid',
  surface: StepSurface = 'banner'
): string => {
  // The modal wording is written to suit both tenses, so it outranks the
  // purchase variant rather than needing one of its own.
  if (surface === 'modal' && MODAL_VARIANT_STEP_KEYS.has(key)) {
    return `${ns}.modal.${key}`
  }
  return saleKind === 'purchase' && PURCHASE_VARIANT_STEP_KEYS.has(key)
    ? `${ns}.purchase.${key}`
    : `${ns}.${key}`
}

/**
 * The same steps as message ids in the listing page's pre-win namespace.
 *
 * Takes the same options as {@link getSellerCollectsWinStepKeys} and forwards
 * them: the bid modal and the banner describe one listing on one page, so a
 * caller that drops an option makes them contradict each other.
 */
export const getSellerCollectsWinSteps = (
  config: SellerCollectsConfig | undefined,
  feeModel: FeeModel,
  saleKind: SaleKind = 'bid',
  options: WinStepOptions = {},
  surface: StepSurface = 'banner'
): string[] =>
  getSellerCollectsWinStepKeys(config, feeModel, options).map((key) =>
    sellerCollectsWinStepId(key, saleKind, surface)
  )
